import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from '../hooks/useTranslation';
import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_ihl0ipc';
const EMAILJS_TEMPLATE_ID = 'template_o56q5mp';
const EMAILJS_PUBLIC_KEY = 'Yv6T_fRIiubFy1bL8';

const ContactSection = () => {
  const { t, language } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Use EmailJS to send email with the configured service, template, and public key
      const templateParams = {
        to_email: 'mohamedabosriea@masellite.com',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'N/A',
        service: formData.service || 'N/A',
        message: formData.message,
        subject: `New Contact Form Submission from ${formData.name}`,
        reply_to: formData.email
      };

      const result = await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

      if (result?.status === 200) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });

        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      console.error(err);
      // Provide clearer error messages when EmailJS fails
      setError(language === 'ar'
        ? `عذراً، حدث خطأ أثناء إرسال الرسالة: ${err.message || 'خطأ غير معروف'}`
        : `Sorry, an error occurred while sending the message: ${err.message || 'unknown error'}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendManualEmail = () => {
    const subject = `New Contact Form Submission from ${formData.name || '[No name]'}`;
    const body = `Name: ${formData.name || ''}%0D%0AEmail: ${formData.email || ''}%0D%0APhone: ${formData.phone || ''}%0D%0AService: ${formData.service || ''}%0D%0AMessage:%0D%0A${formData.message || ''}`;
    window.location.href = `mailto:mohamedabosriea@masellite.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const serviceOptions = language === 'ar' 
    ? [
        'تخطيط الضرائب',
        'التدقيق المالي',
        'مسك الدفاتر',
        'الاستشارات التجارية',
        'خدمات الرواتب'
      ]
    : [
        'Tax Planning',
        'Financial Auditing',
        'Bookkeeping',
        'Business Consulting',
        'Payroll Services'
      ];

  return (
    <section id="contact" className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#A37A00] to-amber-700 bg-clip-text text-transparent"
          >
            {t('contact.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                {t('contact.whyChooseUs.title')}
              </h3>
              <ul className="space-y-3">
                {t('contact.whyChooseUs.points').map((point, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
                {language === 'ar' ? 'معلومات الاتصال' : 'Contact Info'}
              </h3>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <FiPhone className="text-amber-600 dark:text-amber-400" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">
                    {t('contact.contactInfo.call')}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    +20 1270 328 820
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <FiMail className="text-amber-600 dark:text-amber-400" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">
                    {t('contact.contactInfo.email')}
                  </p>
                  <a 
                    href="mailto:mohamedabosriea@masellite.com"
                    className="text-gray-600 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                  >
                    mohamedabosriea@masellite.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <FiMapPin className="text-amber-600 dark:text-amber-400" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">
                    {t('contact.contactInfo.visit')}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {language === 'ar' 
                      ? 'مدينة الشيخ زايد، الجيزة، مصر' 
                      : 'Sheikh Zayed City, Giza, Egypt'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {isSubmitted ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-8 text-center">
                <FiCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-800 dark:text-green-300 mb-2">
                  {t('contact.form.success')}
                </h3>
                <p className="text-green-700 dark:text-green-400 mb-6">
                  {t('contact.form.successMessage')}
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t('contact.form.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder={t('contact.form.placeholder.name')}
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      placeholder={t('contact.form.placeholder.email')}
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                      placeholder={t('contact.form.placeholder.phone')}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('contact.form.service')}
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  >
                    <option value="">{language === 'ar' ? 'اختر الخدمة' : 'Select a service'}</option>
                    {serviceOptions.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
                    placeholder={t('contact.form.placeholder.message')}
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  ></textarea>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                    <div className="mt-3 flex justify-center">
                      <button
                        type="button"
                        onClick={sendManualEmail}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm"
                      >
                        {language === 'ar' ? 'أرسل عبر بريدك' : 'Send via your email client'}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 bg-gradient-to-r from-[#A37A00] to-amber-700 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      <>
                        <FiSend size={20} />
                        {t('contact.form.submit')}
                      </>
                    )}
                  </motion.button>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
                    {t('contact.form.note')}
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;